import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import GrowVerticalIcon from '@atlaskit/icon/core/grow-vertical';
import { ButtonItem } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';

import type { NavItemReorderingProps, NavItemReorderingTriggerProps } from '../../../types';

import messages from './messages';

export function NavItemReorderingMenu({
	TriggerComponent = ButtonItem,
	canMoveUp,
	canMoveDown,
	moveUp,
	moveDown,
	moveToTop,
	moveToBottom,
	onOpenChange,
}: NavItemReorderingProps) {
	const { formatMessage } = useIntl();
	const isOnly = !canMoveUp && !canMoveDown;

	const iconProps = useMemo(() => {
		if (TriggerComponent === ButtonItem) {
			return {
				iconBefore: <GrowVerticalIcon label="" />,
				iconAfter: <ChevronRightIcon color={token('color.icon.subtle')} label="" size="small" />,
			};
		}

		return {
			elemBefore: <GrowVerticalIcon label="" />,
			elemAfter: <ChevronRightIcon color={token('color.icon.subtle')} label="" size="small" />,
		};
	}, [TriggerComponent]);

	return (
		<DropdownMenu
			shouldRenderToParent
			spacing="compact"
			placement="right-start"
			trigger={({ triggerRef, ...triggerProps }: NavItemReorderingTriggerProps) => {
				return (
					<TriggerComponent {...iconProps} {...triggerProps} ref={triggerRef} isDisabled={isOnly}>
						{formatMessage(messages.reorderingMenuTriggerLabel)}
					</TriggerComponent>
				);
			}}
			onOpenChange={onOpenChange}
		>
			<DropdownItemGroup>
				<DropdownItem isDisabled={!canMoveUp} onClick={moveToTop}>
					{formatMessage(messages.moveToTop)}
				</DropdownItem>
				<DropdownItem isDisabled={!canMoveUp} onClick={moveUp}>
					{formatMessage(messages.moveUp)}
				</DropdownItem>
				<DropdownItem isDisabled={!canMoveDown} onClick={moveDown}>
					{formatMessage(messages.moveDown)}
				</DropdownItem>
				<DropdownItem isDisabled={!canMoveDown} onClick={moveToBottom}>
					{formatMessage(messages.moveToBottom)}
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
}
